/**
 * Weekly Q&A, shared between the Masterclasses shelf, the reader, the top-bar
 * search and the community embed.
 *
 * An episode is a row in weekly_qa: a Mux recording plus its questions, each
 * with a timestamp and a written answer, plus a short write-up. The unit of
 * value is the question, so everything here is built around the questions
 * list rather than the recording.
 */
import { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";

export const QA_TAG = "Weekly Q&A";

export function fmtClock(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}` : `${m}:${String(r).padStart(2, "0")}`;
}

export function fmtMinutes(sec) {
  const m = Math.round((Number(sec) || 0) / 60);
  if (!m) return null;
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}min` : `${m} min`;
}

export function fmtWeek(weekOf) {
  if (!weekOf) return null;
  const d = new Date(`${weekOf}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export const qaThumb = (playbackId, w = 640, h = 360) =>
  playbackId ? `https://image.mux.com/${playbackId}/thumbnail.jpg?width=${w}&height=${h}&fit_mode=smartcrop` : null;

// ── Published episodes, fetched once and shared ─────────────────────────
let cache = null, cacheAt = 0, inflight = null;
const TTL = 5 * 60 * 1000;

export async function loadPublishedQa({ force = false } = {}) {
  if (!force && cache && Date.now() - cacheAt < TTL) return cache;
  if (inflight && !force) return inflight;
  inflight = (async () => {
    try {
      const { data, error } = await supabase
        .from("weekly_qa")
        .select("id,title,week_of,mux_playback_id,caption_track_id,duration_sec,questions,summary,published_at")
        .eq("status", "published")
        .order("week_of", { ascending: false });
      if (error) throw error;
      cache = (data || []).map(r => ({ ...r, questions: Array.isArray(r.questions) ? r.questions : [] }));
      cacheAt = Date.now();
      return cache;
    } catch {
      return cache || [];
    } finally { inflight = null; }
  })();
  return inflight;
}

export function invalidateQaCache() { cache = null; cacheAt = 0; }

export function useWeeklyQa(enabled = true) {
  const [rows, setRows] = useState(() => cache || []);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    loadPublishedQa().then(r => { if (alive) setRows(r); });
    return () => { alive = false; };
  }, [enabled]);
  return rows;
}

// ── Shapes the rest of the app already understands ──────────────────────
// A shelf card. The card shows the recording's frame, the title, the first
// questions as its one line, and "Sep 7 · 41 min · 6 questions".
export function qaToItem(row) {
  const qs = row.questions || [];
  return {
    id: `qa-${row.id}`,
    type: "qa",
    qa: row,
    title: row.title,
    sub: qs.slice(0, 3).map(q => q.question).filter(Boolean).join("  ·  "),
    qaQuestions: qs.map(q => q.question).filter(Boolean),
    desc: row.summary || "",
    tag: QA_TAG, category: QA_TAG, level: "All",
    dateLabel: fmtWeek(row.week_of),
    duration: fmtMinutes(row.duration_sec),
    qaCount: qs.length,
    muxPlaybackId: row.mux_playback_id,
    isNew: true,
    date_added: row.published_at,
  };
}

// The reader module. One chapter: the recording up top with the questions
// as its chapters, each question a card with its answer, then the write-up
// at the bottom. The videoLessons block already does the chapter rail, the
// seek and the jump-to transcript, so an episode is just data in that shape.
export function buildQaModule(row) {
  const qs = row.questions || [];
  const summary = (row.summary || "").trim();
  return {
    key: `weekly-qa-${row.id}`,
    title: row.title,
    sub: [fmtWeek(row.week_of) && `Weekly Q&A · ${fmtWeek(row.week_of)}`, `${qs.length} question${qs.length === 1 ? "" : "s"}`]
      .filter(Boolean).join(" · "),
    duration: fmtMinutes(row.duration_sec) || "Q&A",
    chapters: [
      {
        id: "questions",
        title: "The questions",
        blocks: [
          {
            kind: "videoLessons",
            variant: "cards",
            playbackId: row.mux_playback_id,
            textTrackId: row.caption_track_id || undefined,
            segments: qs.map(q => ({
              t: Number(q.t) || 0,
              title: q.question,
              blocks: q.answer ? [{ kind: "text", md: q.answer }] : [],
            })),
          },
          ...(summary ? [
            { kind: "heading", level: 2, text: "The write-up" },
            { kind: "text", md: summary },
          ] : []),
        ],
      },
    ],
  };
}

// Every question as a search hit, so the top bar finds "how do I run a
// 5-out with kids who can't shoot" and lands on the answer, not the episode.
export function qaSearchItems(rows) {
  const out = [];
  for (const row of rows || []) {
    (row.questions || []).forEach((q, i) => {
      if (!q?.question) return;
      out.push({
        id: `qa-${row.id}-${i}`,
        sectionId: "resources",
        title: q.question,
        category: QA_TAG,
        tags: [],
        desc: q.answer || "",
        keyPoints: [],
        muxPlaybackId: row.mux_playback_id,
        duration: fmtClock(q.t),
        qa: { id: row.id, t: Number(q.t) || 0 },
      });
    });
  }
  return out;
}

// What a community post carries. Title, date, the first questions, and a
// frame from the recording. Opening it goes through the Resources page,
// which holds the reader.
export function qaEmbed(row) {
  const qs = row.questions || [];
  return {
    type: "weekly_qa",
    qa_id: row.id,
    title: row.title,
    week_of: row.week_of || null,
    question_count: qs.length,
    questions: qs.slice(0, 3).map(q => q.question).filter(Boolean),
    duration: fmtMinutes(row.duration_sec),
    thumbnail_url: qaThumb(row.mux_playback_id),
  };
}
